import React, { useCallback, useEffect, useState } from 'react';
import { BadgeCheck } from 'lucide-react';
import Button from '../Button';
import FormField from '../form/FormField';
import Select from '../form/Select';
import { cn } from '../cn';
function safeNow() {
    return Date.now();
}
function secondsUntil(iso) {
    if (!iso)
        return 0;
    const ts = new Date(iso).getTime();
    if (!Number.isFinite(ts))
        return 0;
    return Math.max(0, Math.ceil((ts - safeNow()) / 1000));
}
function onlyDigits6(value) {
    return value.replace(/\D/g, '').slice(0, 6);
}
function defaultChannel(emailVerified, phoneVerified) {
    if (emailVerified)
        return 'email';
    if (phoneVerified)
        return 'phone';
    return 'email';
}
export default function WithdrawOtpStep({ variant = 'portal', emailVerified, phoneVerified, maskedEmail, maskedPhone, summaryLine, disabled = false, onSendOtp, onVerifyOtp, onChallengeReady, onError, }) {
    const isPlayer = variant === 'player';
    const canWithdraw = emailVerified || phoneVerified;
    const [channel, setChannel] = useState(() => defaultChannel(emailVerified, phoneVerified));
    const [otp, setOtp] = useState('');
    const [sending, setSending] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [sent, setSent] = useState(false);
    const [verified, setVerified] = useState(false);
    const [nextResendAt, setNextResendAt] = useState(null);
    const [inlineError, setInlineError] = useState(null);
    const [, setTick] = useState(0);
    useEffect(() => {
        setChannel(defaultChannel(emailVerified, phoneVerified));
    }, [emailVerified, phoneVerified]);
    useEffect(() => {
        const t = window.setInterval(() => setTick((x) => x + 1), 250);
        return () => window.clearInterval(t);
    }, []);
    const retrySeconds = secondsUntil(nextResendAt);
    const canRetry = retrySeconds <= 0;
    const labelMuted = isPlayer ? 'text-white/60' : 'text-gray-500';
    const textPrimary = isPlayer ? 'text-white' : 'text-gray-900';
    const borderCard = isPlayer ? 'border-white/20 bg-white/5' : 'border-gray-200 bg-gray-50';
    const inputClass = isPlayer
        ? 'h-11 w-full rounded-lg border border-white/20 bg-white/10 px-3 text-sm font-semibold tracking-[0.3em] text-white outline-none placeholder:font-normal placeholder:tracking-normal placeholder:text-white/40 focus:border-amber-400/60 focus:ring-2 focus:ring-amber-400/20'
        : 'h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm font-semibold tracking-[0.3em] text-gray-900 outline-none ring-0 placeholder:font-normal placeholder:tracking-normal focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100';
    const verifiedStrip = isPlayer
        ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-100'
        : 'border-emerald-200 bg-emerald-50 text-emerald-900';
    const send = useCallback(async () => {
        var _a, _b, _c;
        if (!canWithdraw || disabled)
            return;
        setSending(true);
        setInlineError(null);
        try {
            const res = await onSendOtp(channel);
            setNextResendAt((_a = res.nextResendAt) !== null && _a !== void 0 ? _a : null);
            setSent(true);
        }
        catch (e) {
            const msg = (e === null || e === void 0 ? void 0 : e.message) ||
                ((_c = (_b = e === null || e === void 0 ? void 0 : e.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message) ||
                'Failed to send verification code';
            setInlineError(msg);
            onError === null || onError === void 0 ? void 0 : onError(msg);
        }
        finally {
            setSending(false);
        }
    }, [canWithdraw, disabled, channel, onSendOtp, onError]);
    const verify = useCallback(async () => {
        var _a, _b;
        const cleaned = onlyDigits6(otp);
        if (cleaned.length !== 6 || disabled)
            return;
        setVerifying(true);
        setInlineError(null);
        try {
            const res = await onVerifyOtp(channel, cleaned);
            setVerified(true);
            onChallengeReady === null || onChallengeReady === void 0 ? void 0 : onChallengeReady(res.withdrawChallengeToken);
        }
        catch (e) {
            const msg = (e === null || e === void 0 ? void 0 : e.message) ||
                ((_b = (_a = e === null || e === void 0 ? void 0 : e.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) ||
                'Invalid or expired code';
            setInlineError(msg);
            onError === null || onError === void 0 ? void 0 : onError(msg);
        }
        finally {
            setVerifying(false);
        }
    }, [otp, disabled, channel, onVerifyOtp, onChallengeReady, onError]);
    if (!canWithdraw) {
        return (React.createElement("p", { className: cn('text-xs', isPlayer ? 'text-amber-200' : 'text-amber-800') }, "Verify your email or phone in account settings before withdrawing."));
    }
    const emailLabel = maskedEmail ? `Email (${maskedEmail})` : 'Email';
    const phoneLabel = maskedPhone ? `SMS (${maskedPhone})` : 'SMS (Phone)';
    return (React.createElement("div", { className: "space-y-4" },
        summaryLine ? (React.createElement("div", { className: cn('rounded-lg border p-3 text-xs', borderCard, textPrimary) },
            React.createElement("div", { className: cn('font-semibold uppercase tracking-wide', labelMuted) }, "Summary"),
            React.createElement("div", { className: "mt-1 font-medium" }, summaryLine))) : null,
        React.createElement(FormField, { label: "Receive code via", required: true, state: disabled || verifying ? 'disabled' : 'default' },
            React.createElement(Select, { value: channel, onChange: (e) => {
                    setChannel(e.target.value);
                    setSent(false);
                    setVerified(false);
                    setOtp('');
                    setInlineError(null);
                }, disabled: disabled || verifying, hidePlaceholder: true, options: [
                    {
                        value: 'email',
                        label: emailLabel,
                        disabled: !emailVerified,
                    },
                    {
                        value: 'phone',
                        label: phoneLabel,
                        disabled: !phoneVerified,
                    },
                ] }),
            !emailVerified ? (React.createElement("p", { className: cn('mt-1 text-xs', labelMuted), title: "Verify your email in Settings to use this option." }, "Email option requires verified email.")) : null,
            !phoneVerified ? (React.createElement("p", { className: cn('mt-1 text-xs', labelMuted), title: "Verify your phone in Settings to use this option." }, "SMS option requires verified phone.")) : null),
        React.createElement("div", { className: "flex flex-wrap items-center gap-2" },
            React.createElement(Button, { type: "button", variant: isPlayer ? 'primary' : 'outline', onClick: () => void send(), disabled: disabled || sending || verifying || (!canRetry && sent), loading: sending }, canRetry || !sent ? 'Send verification code' : `Resend in ${retrySeconds}s`),
            sent && canRetry ? (React.createElement("span", { className: cn('text-xs', labelMuted) }, "You can request a new code.")) : null),
        React.createElement("div", null,
            React.createElement("label", { className: cn('mb-1.5 block text-sm font-semibold', textPrimary) }, "Verification code"),
            React.createElement("input", { value: otp, onChange: (e) => setOtp(onlyDigits6(e.target.value)), inputMode: "numeric", autoComplete: "one-time-code", placeholder: "6-digit code", disabled: disabled || verifying, className: inputClass, "aria-label": "Verification code" }),
            inlineError ? React.createElement("p", { className: "mt-1.5 text-xs text-rose-600" }, inlineError) : null),
        !verified ? (React.createElement(Button, { type: "button", variant: "primary", onClick: () => void verify(), disabled: disabled || verifying || onlyDigits6(otp).length !== 6, loading: verifying }, "Verify code")) : (React.createElement("div", { className: cn('flex items-center gap-2 rounded-lg border px-3 py-2 text-sm', verifiedStrip) },
            React.createElement(BadgeCheck, { className: "h-5 w-5 shrink-0", "aria-hidden": true }),
            React.createElement("span", null, "Identity verified. You can submit your withdrawal.")))));
}
